// src/pages/BuyWorkout.tsx
// Public purchase page — no auth required
'use client';
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Dumbbell, Clock, Zap, Shield, Star, ChevronRight, Loader2
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { ENV } from "@/lib/env";

// ── Types ──────────────────────────────────────────────────────

interface WorkoutProduct {
  id: string;
  title: string;
  description: string | null;
  cover_image_url: string | null;
  sport_type: string;
  difficulty: string;
  estimated_duration_minutes: number | null;
  program_weeks: any;
  num_weeks: number;
  product_type: string;
  billing_interval: string | null;
  price_amount: number;
  currency: string;
  share_code: string;
  preview_notes: string | null;
  purchase_count: number;
  subscriber_count: number;
  partner_accounts: {
    brand_name: string | null;
    logo_url: string | null;
    accent_colour: string | null;
  } | null;
}

// ── Helpers ──────────────────────────────────────────────────────

const SPORT_LABELS: Record<string, string> = {
  strength: 'Strength', running: 'Running', cycling: 'Cycling',
  swimming: 'Swimming', crossfit: 'CrossFit', hiit: 'HIIT',
  yoga: 'Yoga', bodybuilding: 'Bodybuilding',
};

function formatPrice(amount: number, currency: string) {
  const symbol = currency === 'gbp' ? '£' : currency === 'usd' ? '$' : '€';
  return `${symbol}${amount.toFixed(2)}`;
}

function getSessionCount(programWeeks: any): number {
  if (!Array.isArray(programWeeks)) return 0;
  return programWeeks.reduce((total: number, week: any) => {
    return total + (week.days || []).reduce((dayTotal: number, day: any) => {
      return dayTotal + (day.sessions || []).length;
    }, 0);
  }, 0);
}

function formatPriceLabel(product: WorkoutProduct): string {
  const symbol = product.currency === 'gbp' ? '£' : product.currency === 'usd' ? '$' : '€';
  const price = `${symbol}${product.price_amount.toFixed(2)}`;
  if (product.product_type === 'subscription') {
    return `${price}/${product.billing_interval === 'monthly' ? 'month' : 'week'}`;
  }
  return price;
}

// ── Component ──────────────────────────────────────────────────────

export default function BuyWorkout() {
  const { shareCode } = useParams<{ shareCode: string }>();
  const [product, setProduct] = useState<WorkoutProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [email, setEmail] = useState('');
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      if (!supabase || !shareCode) return;
      const { data, error } = await supabase
        .from('partner_workout_products')
        .select('*, partner_accounts(brand_name, logo_url, accent_colour)')
        .eq('share_code', shareCode)
        .eq('is_published', true)
        .maybeSingle();

      if (error || !data) {
        setNotFound(true);
      } else {
        setProduct(data);
      }
      setLoading(false);
    })();
  }, [shareCode]);

  async function handlePurchase() {
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    if (!product) return;

    setPurchasing(true);
    setError('');

    try {
      const res = await fetch(`${ENV.EDGE_BASE}/create-workout-purchase-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          share_code: product.share_code,
          buyer_email: email.trim(),
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong');
        setPurchasing(false);
        return;
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError('Failed to create checkout session');
        setPurchasing(false);
      }
    } catch (err) {
      setError('Network error — please try again');
      setPurchasing(false);
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#ffffff] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#a78bfa] animate-spin" />
      </div>
    );
  }

  // Not found
  if (notFound || !product) {
    return (
      <div className="min-h-screen bg-[#ffffff] flex items-center justify-center text-[#0f172a]">
        <div className="text-center">
          <Dumbbell className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Workout Not Found</h1>
          <p className="text-gray-500">This workout is no longer available or the link is incorrect.</p>
        </div>
      </div>
    );
  }

  const brandName = product.partner_accounts?.brand_name || 'Coach';
  const accentColor = product.partner_accounts?.accent_colour || '#a78bfa';
  const logoUrl = product.partner_accounts?.logo_url;
  const sessionCount = getSessionCount(product.program_weeks);
  const isSubscription = product.product_type === 'subscription';

  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
      {/* Header */}
      <header className="border-b border-[#e2e8f0] px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-[#0f172a] font-bold text-lg"
              style={{ backgroundColor: accentColor }}
            >
              {brandName.charAt(0)}
            </div>
          )}
          <span className="font-semibold text-lg">{brandName}</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Cover */}
        <div className="rounded-2xl overflow-hidden mb-8">
          {product.cover_image_url ? (
            <img
              src={product.cover_image_url}
              alt={product.title}
              className="w-full h-64 object-cover"
            />
          ) : (
            <div
              className="w-full h-64 flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${accentColor}33, #ffffff)` }}
            >
              <Dumbbell className="w-20 h-20" style={{ color: accentColor }} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          {/* Left: Details */}
          <div className="md:col-span-3 space-y-6">
            <div>
              <h1 className="text-3xl font-bold mb-3">{product.title}</h1>
              <div className="flex flex-wrap items-center gap-3">
                {isSubscription && (
                  <span className="text-sm bg-purple-500/20 text-purple-400 px-3 py-1 rounded-lg font-medium">
                    Subscription
                  </span>
                )}
                <span className="text-sm bg-[#f1f5f9] px-3 py-1 rounded-lg">
                  {SPORT_LABELS[product.sport_type] || product.sport_type}
                </span>
                <span className="text-sm bg-[#f1f5f9] px-3 py-1 rounded-lg capitalize">
                  {product.difficulty}
                </span>
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <Clock className="w-4 h-4" /> {product.num_weeks || 1} week{(product.num_weeks || 1) !== 1 ? 's' : ''}
                </span>
                {sessionCount > 0 && (
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <Dumbbell className="w-4 h-4" /> {sessionCount} sessions
                  </span>
                )}
              </div>
            </div>

            {product.description && (
              <div>
                <h2 className="font-semibold text-lg mb-2">About This Workout</h2>
                <p className="text-gray-600 whitespace-pre-line">{product.description}</p>
              </div>
            )}

            {product.preview_notes && (
              <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-5">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Star className="w-4 h-4" style={{ color: accentColor }} />
                  What's Included
                </h3>
                <p className="text-gray-600 whitespace-pre-line">{product.preview_notes}</p>
              </div>
            )}

            {/* Trust signals */}
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <Shield className="w-4 h-4" /> Secure checkout
              </span>
              <span className="flex items-center gap-1.5">
                <Zap className="w-4 h-4" /> Instant access
              </span>
              {product.purchase_count > 0 && (
                <span className="flex items-center gap-1.5">
                  <Star className="w-4 h-4" /> {product.purchase_count} sold
                </span>
              )}
            </div>
          </div>

          {/* Right: Purchase Card */}
          <div className="md:col-span-2">
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-6 sticky top-6">
              <div className="text-center mb-6">
                <p className="text-4xl font-bold" style={{ color: accentColor }}>
                  {formatPriceLabel(product)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {isSubscription ? `Cancel anytime · ${product.num_weeks || 1} weeks of programming` : 'One-time purchase'}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Your email</label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 bg-[#ffffff] border border-[#e2e8f0] rounded-xl text-[#0f172a] placeholder-gray-600 focus:border-[#a78bfa]/50 focus:outline-none transition"
                    placeholder="you@email.com"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(''); }}
                  />
                </div>

                {error && (
                  <p className="text-red-400 text-sm">{error}</p>
                )}

                <button
                  onClick={handlePurchase}
                  disabled={purchasing}
                  className="w-full py-3.5 rounded-xl font-semibold text-[#0f172a] text-lg flex items-center justify-center gap-2 transition disabled:opacity-50"
                  style={{ backgroundColor: accentColor }}
                >
                  {purchasing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {isSubscription ? 'Subscribe Now' : 'Buy Now'} <ChevronRight className="w-5 h-5" />
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-600 text-center">
                  Powered by Stripe. {isSubscription
                    ? 'New programming delivered every week. Cancel anytime.'
                    : "You'll receive the full program immediately after payment."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
